import { Workbook, Worksheet } from 'exceljs';

// --- Plain data shapes (no Angular / no FormGroup) -----------------------------
// The Angular components adapt their reactive-form values to/from these shapes.
// Keeping the build/parse/diff logic over plain objects makes it runnable under
// plain Node (see scripts/test-xlsx) and unit-testable without bootstrapping
// Angular.

export interface ProductRow {
  id: string;
  name: string;
  category: string;
  cost: number;
  bespoke: boolean;
  invoiceLabel: string;
  description: string;
  note: string;
}

export interface SelectOption {
  name: string;
  value: string;
}

export interface BusinessField {
  key: string;
  label: string;
  // The human-readable display value written to the sheet.
  value: string;
  // When present, the field is backed by a {name, value} object select.
  // The sheet shows `name`; import maps the chosen name back via these options.
  options?: SelectOption[];
}

export interface ServiceData {
  // Stable id of this service INSTANCE (the service wrapper's _meta.id). This is
  // the real identity — a provider can hold two services of the same type (e.g.
  // two "Audio Visual"), so we key on this, never on `type` or the sheet name.
  serviceId: string;
  // service type key, e.g. 'music' (DynamicComponents value)
  type: string;
  // human label used as the sheet name, e.g. 'Music'
  label: string;
  products: ProductRow[];
  // Existing category display names for this service type, used to build the
  // Category dropdown. New values typed by the user are still accepted.
  categoryOptions?: string[];
}

export interface ProviderData {
  id: number | null;
  business: BusinessField[];
  services: ServiceData[];
}

// A row parsed back out of a service sheet. `delete` reflects the Delete? column.
export interface ParsedProductRow extends ProductRow {
  delete: boolean;
}

export interface ParsedService {
  // Service instance id read back from the sheet's hidden metadata cell. Empty
  // only for sheets that predate the serviceId stamp or were hand-created.
  serviceId: string;
  type: string;
  label: string;
  rows: ParsedProductRow[];
}

export interface ParsedWorkbook {
  providerId: number | null;
  business: BusinessField[];
  services: ParsedService[];
}

export interface ProductChange {
  serviceId: string; // which service INSTANCE this change targets
  type: string; // service type key
  label: string; // service sheet label
  row: ParsedProductRow;
  previous?: ProductRow; // present for updates/deletes
}

export interface BusinessChange {
  key: string;
  label: string;
  previous: string;
  next: string;
}

export interface DiffResult {
  business: BusinessChange[];
  created: ProductChange[];
  updated: ProductChange[];
  deleted: ProductChange[];
  errors: string[];
}

// --- Constants -----------------------------------------------------------------

const ID_COL = 1; // column A holds the hidden internal id
const BUSINESS_SHEET = 'Business Details';
const PROVIDER_ID_KEY = '__providerId';

// Hidden metadata stamped on cell A1 of each service sheet, so import can map a
// sheet back to its exact service instance and type WITHOUT reconstructing them
// from the (user-visible, possibly duplicated) sheet name. Format:
//   __service=<serviceId>|<type>
const SERVICE_META_KEY = '__service';

type ServiceColumnKey = keyof ProductRow | 'delete';

// Order matters: column index -> field. ID first (hidden), Delete? last.
const SERVICE_COLUMNS: { key: ServiceColumnKey; header: string }[] = [
  { key: 'id', header: 'id' },
  { key: 'name', header: 'Name' },
  { key: 'category', header: 'Category' },
  { key: 'cost', header: 'Cost' },
  { key: 'bespoke', header: 'Bespoke' },
  { key: 'invoiceLabel', header: 'Invoice Label' },
  { key: 'description', header: 'Description' },
  { key: 'note', header: 'Note' },
  { key: 'delete', header: 'Delete?' },
];

// 1-based column index of a service column (Excel/ExcelJS columns are 1-based).
function serviceCol(key: ServiceColumnKey): number {
  return SERVICE_COLUMNS.findIndex((c) => c.key === key) + 1;
}

// Mirror of the client's stringToKey so category matching is identical.
// Kept null-safe (the client version is too) so both sides normalise the same.
export function stringToKey(str: string): string {
  return (str || '').toLowerCase().replace(/\s+/g, '-');
}

// Stable key identifying a single product to delete. Product ids are only
// unique WITHIN a service, so the owning service instance is part of the key.
// Used both to record the user's delete toggles and to look them up on apply.
export function deleteKey(serviceId: string, productId: string): string {
  return `${serviceId}::${productId}`;
}

// --- Build (export) ------------------------------------------------------------

export function buildWorkbook(provider: ProviderData): Workbook {
  const workbook = new Workbook();

  buildBusinessSheet(workbook, provider);

  // Excel sheet names must be unique and <=31 chars. A provider can hold two
  // services of the same type (e.g. two "Audio Visual"), so derive a unique,
  // human-readable sheet name per service. The true identity travels in the
  // hidden metadata cell, not the name.
  const usedNames = new Set<string>();
  for (const service of provider.services) {
    const sheetName = uniqueSheetName(service.label, usedNames);
    buildServiceSheet(workbook, service, sheetName);
  }

  return workbook;
}

// Produce a sheet name that is <=31 chars and unique within `used`, appending
// " (2)", " (3)" … on collision. Mutates `used` with the chosen name.
function uniqueSheetName(label: string, used: Set<string>): string {
  const clip = (s: string) => s.slice(0, 31);
  const base = clip((label || 'Service').trim()) || 'Service';

  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  for (let n = 2; ; n++) {
    const suffix = ` (${n})`;
    const candidate = clip(base.slice(0, 31 - suffix.length)) + suffix;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
}

function buildBusinessSheet(workbook: Workbook, provider: ProviderData): void {
  const sheet = workbook.addWorksheet(BUSINESS_SHEET);

  sheet.columns = [
    { header: 'Field', key: 'label', width: 28 },
    { header: 'Value', key: 'value', width: 50 },
  ];

  provider.business.forEach((field, i) => {
    sheet.addRow({ label: field.label, value: field.value });

    // For select-backed fields, attach a dropdown of the allowed display names.
    if (field.options && field.options.length) {
      const rowNumber = i + 2; // row 1 is the header
      sheet.getCell(`B${rowNumber}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${field.options.map((o) => o.name).join(',')}"`],
        showErrorMessage: true,
        errorStyle: 'error',
        error: 'Please choose a value from the list.',
      };
    }
  });

  // Stash the provider id (and each field key) so import can map values back
  // without showing the user any internal keys. We keep them in a hidden,
  // very narrow third column rather than a visible row.
  sheet.getColumn(3).key = 'key';
  sheet.getColumn(3).hidden = true;
  sheet.getCell('C1').value = `${PROVIDER_ID_KEY}=${provider.id ?? ''}`;
  provider.business.forEach((field, i) => {
    // data rows start at row 2 (row 1 is the header)
    sheet.getCell(`C${i + 2}`).value = field.key;
  });

  styleHeader(sheet, 2);
}

function buildServiceSheet(workbook: Workbook, service: ServiceData, sheetName: string): void {
  const sheet = workbook.addWorksheet(sheetName);

  sheet.columns = SERVICE_COLUMNS.map((col) => ({
    header: col.header,
    key: col.key as string,
    width: col.key === 'description' ? 40 : col.key === 'invoiceLabel' ? 24 : 18,
  }));

  for (const product of service.products) {
    sheet.addRow({
      id: product.id,
      name: product.name,
      category: product.category,
      cost: product.cost,
      bespoke: product.bespoke ? 'Yes' : 'No',
      invoiceLabel: product.invoiceLabel,
      description: product.description,
      note: product.note,
      delete: 'No',
    });
  }

  // Force two decimal places on the Cost column so e.g. 500.99 isn't shown as
  // 501. numFmt is display only — the underlying value stays exact.
  sheet.getColumn(serviceCol('cost')).numFmt = '0.00';

  // Hide and lock the id column so users never see the internal id-... keys,
  // but it stays in the file so import can match rows back reliably.
  sheet.getColumn(ID_COL).hidden = true;

  // Stamp the service identity into the (hidden) id-column header cell. The
  // parser skips row 1 and matches columns by position, so overwriting the "id"
  // header text here is harmless — and it lets import resolve the exact service
  // instance + type instead of guessing from the sheet name.
  sheet.getCell(1, ID_COL).value = `${SERVICE_META_KEY}=${service.serviceId}|${service.type}`;

  // Dropdowns per data row: Yes/No on Bespoke + Delete?, and the existing
  // categories on Category (warn-only — a new typed value is still accepted).
  const bespokeColLetter = colLetter(serviceCol('bespoke'));
  const deleteColLetter = colLetter(serviceCol('delete'));
  const categoryColLetter = colLetter(serviceCol('category'));

  // Build the category list formula once; reuse for each row if it fits Excel's
  // inline list limit (~255 chars). If it's too long, skip the dropdown.
  const categoryFormula = buildInlineListFormula(service.categoryOptions ?? []);

  const lastRow = service.products.length + 1; // +1 for header
  for (let r = 2; r <= Math.max(lastRow, 2); r++) {
    applyYesNoDropdown(sheet, `${bespokeColLetter}${r}`);
    applyYesNoDropdown(sheet, `${deleteColLetter}${r}`);
    if (categoryFormula) {
      sheet.getCell(`${categoryColLetter}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [categoryFormula],
        // Warn-only: showErrorMessage false lets a new category through.
        showErrorMessage: false,
      };
    }
  }

  styleHeader(sheet, SERVICE_COLUMNS.length);
}

function styleHeader(sheet: Worksheet, columnCount: number): void {
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };

  // Freeze the header and enable autofilter across the populated columns.
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columnCount },
  };

  // Protect the sheet but unlock data cells so the user can only edit values,
  // not the header row (which carries the column meaning). ExcelJS locks all
  // cells by default once protection is on; we unlock everything below row 1.
  headerRow.eachCell((cell) => {
    cell.protection = { locked: true };
  });
}

function applyYesNoDropdown(sheet: Worksheet, address: string): void {
  sheet.getCell(address).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: ['"Yes,No"'],
    showErrorMessage: true,
    errorStyle: 'error',
    error: 'Please choose Yes or No.',
  };
}

// --- Parse (import) ------------------------------------------------------------

export async function parseWorkbook(buffer: ArrayBuffer | Buffer): Promise<ParsedWorkbook> {
  const workbook = new Workbook();
  await workbook.xlsx.load(buffer as any);

  const business: BusinessField[] = [];
  let providerId: number | null = null;
  const services: ParsedService[] = [];

  workbook.eachSheet((sheet) => {
    if (sheet.name === BUSINESS_SHEET) {
      const parsed = parseBusinessSheet(sheet);
      providerId = parsed.providerId;
      business.push(...parsed.fields);
    } else {
      services.push(parseServiceSheet(sheet));
    }
  });

  return { providerId, business, services };
}

function parseBusinessSheet(sheet: Worksheet): {
  providerId: number | null;
  fields: BusinessField[];
} {
  const fields: BusinessField[] = [];
  let providerId: number | null = null;

  const idCell = cellString(sheet.getCell('C1').value);
  if (idCell.startsWith(`${PROVIDER_ID_KEY}=`)) {
    const raw = idCell.slice(`${PROVIDER_ID_KEY}=`.length).trim();
    providerId = raw ? Number(raw) : null;
  }

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return; // header
    }
    const label = cellString(row.getCell(1).value);
    const value = cellString(row.getCell(2).value);
    const key = cellString(row.getCell(3).value);
    if (!key && !label) {
      return;
    }
    fields.push({ key: key || stringToKey(label), label, value });
  });

  return { providerId, fields };
}

function parseServiceSheet(sheet: Worksheet): ParsedService {
  const rows: ParsedProductRow[] = [];

  // Resolve the service identity from the hidden metadata stamped on A1 at
  // export. Fall back to deriving the type from the sheet name for sheets that
  // predate the stamp or were hand-created (serviceId then stays empty, and the
  // diff matches such a sheet by type — single-instance behaviour as before).
  const meta = parseServiceMeta(sheet.getCell(1, ID_COL).value);
  const serviceId = meta.serviceId;
  const type = meta.type || stringToKey(sheet.name);

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return; // header
    }

    const id = cellString(row.getCell(serviceCol('id')).value);
    const name = cellString(row.getCell(serviceCol('name')).value);
    const category = cellString(row.getCell(serviceCol('category')).value);

    // Skip genuinely empty template rows. A row with an id is a known product
    // (matched on import) even if its visible cells were cleared — e.g. a row
    // marked Delete? = Yes with the name blanked out must still be seen.
    if (!id && !name && !category) {
      return;
    }

    rows.push({
      id,
      name,
      category,
      cost: parseCost(row.getCell(serviceCol('cost')).value),
      bespoke: parseYesNo(row.getCell(serviceCol('bespoke')).value),
      invoiceLabel: cellString(row.getCell(serviceCol('invoiceLabel')).value),
      description: cellString(row.getCell(serviceCol('description')).value),
      note: cellString(row.getCell(serviceCol('note')).value),
      delete: parseYesNo(row.getCell(serviceCol('delete')).value),
    });
  });

  return { serviceId, type, label: sheet.name, rows };
}

// Reads `__service=<serviceId>|<type>` back out of a service sheet's metadata
// cell. Returns blanks if the cell is absent or not in the expected shape.
function parseServiceMeta(cellValue: unknown): { serviceId: string; type: string } {
  const text = cellString(cellValue);
  if (!text.startsWith(`${SERVICE_META_KEY}=`)) {
    return { serviceId: '', type: '' };
  }
  const payload = text.slice(`${SERVICE_META_KEY}=`.length);
  const [serviceId = '', type = ''] = payload.split('|');
  return { serviceId: serviceId.trim(), type: type.trim() };
}

// --- Diff ----------------------------------------------------------------------

export function diff(provider: ProviderData, parsed: ParsedWorkbook): DiffResult {
  const result: DiffResult = {
    business: [],
    created: [],
    updated: [],
    deleted: [],
    errors: [],
  };

  diffBusiness(provider, parsed, result);
  diffServices(provider, parsed, result);

  return result;
}

function diffBusiness(provider: ProviderData, parsed: ParsedWorkbook, result: DiffResult): void {
  const currentByKey = new Map(provider.business.map((f) => [f.key, f]));

  for (const field of parsed.business) {
    const current = currentByKey.get(field.key);
    if (!current) {
      continue; // unknown business field, ignore
    }
    if ((current.value ?? '') !== (field.value ?? '')) {
      result.business.push({
        key: field.key,
        label: current.label,
        previous: current.value ?? '',
        next: field.value ?? '',
      });
    }
  }
}

function diffServices(provider: ProviderData, parsed: ParsedWorkbook, result: DiffResult): void {
  // Primary match is by service instance id. A type-keyed map is the fallback
  // for legacy sheets with no stamped serviceId — but only when the provider
  // holds a SINGLE service of that type, otherwise we can't tell which one the
  // sheet meant and must not guess.
  const servicesById = new Map(provider.services.map((s) => [s.serviceId, s]));
  const typeCounts = new Map<string, number>();
  for (const s of provider.services) {
    typeCounts.set(s.type, (typeCounts.get(s.type) ?? 0) + 1);
  }
  const soleServiceOfType = (type: string) =>
    typeCounts.get(type) === 1 ? provider.services.find((s) => s.type === type) : undefined;

  for (const parsedService of parsed.services) {
    const current = parsedService.serviceId
      ? servicesById.get(parsedService.serviceId)
      : soleServiceOfType(parsedService.type);

    if (!current) {
      // Either an unknown type, or an ambiguous legacy sheet (duplicate type,
      // no serviceId to disambiguate). Surface it rather than touch the wrong one.
      result.errors.push(
        `Unknown or ambiguous sheet "${parsedService.label}" — no matching service.`
      );
      continue;
    }

    const productsById = new Map(current.products.map((p) => [p.id.toLowerCase(), p]));
    const validCategories = new Set(current.products.map((p) => stringToKey(p.category)));

    const tag = { serviceId: current.serviceId, type: current.type, label: parsedService.label };

    for (const row of parsedService.rows) {
      const existing = row.id ? productsById.get(row.id.toLowerCase()) : undefined;

      if (row.delete) {
        if (existing) {
          result.deleted.push({ ...tag, row, previous: existing });
        }
        // Delete?=Yes on a row with no matching id is a no-op.
        continue;
      }

      // Validation: cost must be a finite number (!isFinite already covers NaN).
      if (!Number.isFinite(row.cost)) {
        result.errors.push(
          `${parsedService.label}: "${row.name || row.id}" has an invalid cost.`
        );
        continue;
      }

      if (!row.category) {
        result.errors.push(`${parsedService.label}: "${row.name || row.id}" is missing a category.`);
        continue;
      }

      if (existing) {
        if (productChanged(existing, row)) {
          result.updated.push({ ...tag, row, previous: existing });
        }
      } else {
        // New product — note if it introduces a new category (allowed; the
        // import flow creates categories as needed, same as today's CSV import).
        if (!validCategories.has(stringToKey(row.category))) {
          validCategories.add(stringToKey(row.category));
        }
        result.created.push({ ...tag, row });
      }
    }
  }
}

function productChanged(a: ProductRow, b: ParsedProductRow): boolean {
  return (
    (a.name ?? '') !== (b.name ?? '') ||
    stringToKey(a.category) !== stringToKey(b.category) ||
    Number(a.cost) !== Number(b.cost) ||
    !!a.bespoke !== !!b.bespoke ||
    (a.invoiceLabel ?? '') !== (b.invoiceLabel ?? '') ||
    (a.description ?? '') !== (b.description ?? '') ||
    (a.note ?? '') !== (b.note ?? '')
  );
}

// --- Cell helpers --------------------------------------------------------------

function cellString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    // ExcelJS rich text / formula / hyperlink objects.
    const obj = value as any;
    if (typeof obj.text === 'string') {
      return obj.text;
    }
    if (typeof obj.result !== 'undefined') {
      return String(obj.result);
    }
    if (Array.isArray(obj.richText)) {
      return obj.richText.map((rt: any) => rt.text).join('');
    }
  }
  return String(value).trim();
}

function parseCost(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }
  const str = cellString(value).replace(/[$,\s]/g, '');
  if (str === '') {
    return 0;
  }
  return Number(str);
}

function parseYesNo(value: unknown): boolean {
  const str = cellString(value).toLowerCase();
  return str === 'yes' || str === 'y' || str === 'true' || str === '1';
}

// Builds an Excel inline list formula `"a,b,c"` from option names, or null if
// the list is empty or exceeds Excel's ~255-char inline limit (in which case
// the column is left as free text rather than a broken dropdown).
function buildInlineListFormula(options: string[]): string | null {
  const unique = Array.from(new Set(options.filter((o) => o && o.trim().length)));
  if (!unique.length) {
    return null;
  }
  // Excel inline lists can't contain commas inside values; drop any that do.
  const safe = unique.filter((o) => !o.includes(','));
  const joined = safe.join(',');
  if (!joined.length || joined.length > 250) {
    return null;
  }
  return `"${joined}"`;
}

function colLetter(index: number): string {
  // 1 -> A, 2 -> B, ...
  let result = '';
  let n = index;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}
